import React, { useState } from 'react';
import { Box, Collapse, Stack, Tooltip, Typography } from '@mui/material';
import { ExpandLess, ExpandMore, HourglassEmpty } from '@mui/icons-material';
import { QuestionInsight } from '../insightsTypes';
import { INSIGHT_REGISTRY } from '../insightsRegistry';

interface QuestionAccordionRowProps {
    insight: QuestionInsight;
    number: number;
    defaultExpanded?: boolean;
}

const QuestionAccordionRow: React.FC<QuestionAccordionRowProps> = ({ insight, number, defaultExpanded = false }) => {
    const [expanded, setExpanded] = useState(defaultExpanded);

    // A question only has insights once at least one analyzed call produced an answer.
    // Without insights there is nothing to show, so the row is not expandable.
    const hasInsight = insight.overall.responseCount > 0;

    const isOpen = hasInsight && expanded;
    const toggle = () => {
        if (hasInsight) {
            setExpanded((prev) => !prev);
        }
    };

    const interactiveProps = hasInsight
        ? {
              role: 'button',
              tabIndex: 0,
              'aria-expanded': expanded,
              onClick: toggle,
              onKeyDown: (event: React.KeyboardEvent) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      toggle();
                  }
              },
          }
        : {};

    return (
        <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
            <Box
                {...interactiveProps}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 3,
                    px: '20px',
                    py: '15px',
                    cursor: hasInsight ? 'pointer' : 'default',
                    bgcolor: isOpen ? 'gray.10' : 'transparent',
                    '&:hover': hasInsight ? { bgcolor: 'gray.10' } : undefined,
                    '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: '-2px' },
                }}
            >
                <Stack direction="row" spacing={0.8} alignItems="center" sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.disabled', minWidth: 14, textAlign: 'center' }}>
                        {number}
                    </Typography>

                    <Typography sx={{ fontSize: 14.5, fontWeight: 600 }} noWrap title={insight.questionText}>
                        {insight.questionText}
                    </Typography>
                </Stack>
                <Stack direction="row" spacing={2.75} alignItems="center" sx={{ flexShrink: 0 }}>
                    {insight?.overall?.responseCount > 0 && (
                        <Typography sx={{ fontSize: 12.5, color: 'text.disabled', display: { xs: 'none', sm: 'block' } }}>
                            {insight.overall.responseCount.toLocaleString()} responses
                        </Typography>
                    )}

                    {hasInsight ? (
                        <>
                            {/* An empty title renders no tooltip, so a type with no
                                explanation to give simply shows the figure. */}
                            <Tooltip title={INSIGHT_REGISTRY[insight.type].summaryMetricInfo ?? ''} arrow placement="top">
                                <Typography
                                    sx={{ fontSize: 13.5, fontWeight: 700, minWidth: 60, textAlign: 'right' }}
                                    color={'text.secondary'}
                                >
                                    {INSIGHT_REGISTRY[insight.type].summaryMetric(insight)}
                                </Typography>
                            </Tooltip>
                            <Box sx={{ display: 'flex', color: 'text.disabled' }}>
                                {expanded ? <ExpandLess sx={{ fontSize: 22 }} /> : <ExpandMore sx={{ fontSize: 22 }} />}
                            </Box>
                        </>
                    ) : (
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            <HourglassEmpty sx={{ fontSize: 15, color: 'text.disabled' }} />
                            <Typography sx={{ fontSize: 13, fontStyle: 'italic', color: 'text.disabled' }}>Awaiting insights</Typography>
                        </Stack>
                        // <Stack direction="row" spacing={0.75} alignItems="center">
                        //     <HourglassEmpty sx={{ fontSize: 15, color: 'text.disabled' }} />
                        //     <Typography sx={{ fontSize: 13, fontStyle: 'italic', color: 'text.disabled' }}>No insights yet</Typography>
                        // </Stack>
                    )}
                </Stack>
            </Box>

            <Collapse in={isOpen}>
                <Box
                    sx={{
                        px: 2,
                        py: 1.5,
                    }}
                >
                    {INSIGHT_REGISTRY[insight.type].renderCard(insight)}
                </Box>
            </Collapse>
        </Box>
    );
};

export default QuestionAccordionRow;
